import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import illustration from '../../../assets/message-illustration.svg';
import { useAppDispatch, useAppSelector } from '../../../store';
import { parseMessagesData } from '../../../utils/general';
import { selectAccountState } from '../../account/api/accountSlice';
import { getMessagesThunk, selectInboxState, selectMessageByThreadId } from '../api/inboxSlice';
import MessagesList from './MessageList';
import MessageThreadList from './MessageThreadList';

import '../Messages.scss';
import { Spinner } from 'react-bootstrap';
import { MOBILE_BREAKPOINT } from '../../../config';

const MESSAGES_PER_PAGE = 10;
const POLLING_INTERVAL_DURATION = 3000;

const InboxMessages: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { threadId } = useParams();
  const { messageThreadActive, messages, total } = useAppSelector(selectInboxState);
  const { patient } = useAppSelector(selectAccountState);
  const selectedMessageByThreadId = useAppSelector(selectMessageByThreadId(threadId));

  const abortControllerRef = useRef<AbortController>(new AbortController());

  const [isLoading, setIsLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [isThreadView, setIsThreadView] = useState(false);

  const [hasMessages, parsedMessages] = useMemo(
    () => [!!messages.length, parseMessagesData(messages)],
    [messages],
  );

  const loadChunkOfMessages = useCallback(async () => {
    if (!patient?.id || messages.length >= total) return;

    setIsLoading(true);
    const pageNumber = Math.floor(messages.length / MESSAGES_PER_PAGE);

    if (abortControllerRef.current.signal.aborted) {
      abortControllerRef.current = new AbortController();
    }

    try {
      const data = await dispatch(
        getMessagesThunk({
          personId: patient.id,
          limit: MESSAGES_PER_PAGE,
          offset: pageNumber * MESSAGES_PER_PAGE,
          abortController: abortControllerRef.current,
        }),
      ).unwrap();

      if (data?.resource?.entry?.length < MESSAGES_PER_PAGE) setHasNextPage(false);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, messages.length, patient?.id, total]);

  const checkIfMessageLoaded = useCallback(
    (index: number) => {
      return !hasNextPage || index < messages.length;
    },
    [hasNextPage, messages.length],
  );

  useEffect(() => {
    loadChunkOfMessages();
  }, [loadChunkOfMessages]);

  useEffect(() => {
    if (!patient?.id) return;
    const pollingInterval = setInterval(() => {
      dispatch(
        getMessagesThunk({
          personId: patient.id,
          limit: MESSAGES_PER_PAGE,
          offset: 0,
        }),
      );
      setIsLoading(false);
    }, POLLING_INTERVAL_DURATION);
    return () => clearInterval(pollingInterval);
  }, [dispatch, patient]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkIsThreadView = () => {
      setIsThreadView(window.location.pathname.match(/^\/app\/messages\/inbox\/[^/]+$/) !== null);
    };
    checkIsThreadView();
    window.addEventListener('popstate', checkIsThreadView);
    return () => window.removeEventListener('popstate', checkIsThreadView);
  }, []);

  const listWrapperStyle = useMemo(
    () => ({
      display: isMobile && isThreadView ? 'none' : 'flex',
    }),
    [isMobile, isThreadView],
  );

  return (
    <div className='messages__inbox-wrapper'>
      <div className='messages__list-wrapper' style={listWrapperStyle}>
        {isLoading && !hasMessages && (
          <div className='spinner-container'>
            <Spinner className='spinner-icon' />
          </div>
        )}
        {!hasMessages && !isLoading && (
          <div className='messages__empty-wrapper border-end border-gray-200'>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <img src={illustration} alt='illustration' />
              <p className='messages__empty'>{t('This folder is empty.')}</p>
              <p className='message__empty-subtitle'>{t('Try checking another one.')}</p>
            </div>
          </div>
        )}
        {hasMessages && (
          <MessagesList
            items={parsedMessages}
            currentUserId={patient?.id || ''}
            isItemLoaded={checkIfMessageLoaded}
            loadMoreItems={loadChunkOfMessages}
            activeMessageID={selectedMessageByThreadId?.id}
          />
        )}
      </div>
      {messageThreadActive && <MessageThreadList />}
    </div>
  );
};

export default InboxMessages;
